/**
 * Global Alpine component for product cards.
 * Registered once — x-data="moodProductCard" references this,
 * avoiding inline function compilation for every card on the page.
 */
document.addEventListener('alpine:init', () => {
    if (typeof Alpine === 'undefined') return;

    Alpine.data('moodProductCard', () => ({
        async addToCart(e) {
            e.preventDefault();
            e.target.disabled = true;
            const variantId = e.target.dataset.variantId;
            const payload = {
                items: [{ id: parseInt(variantId, 10), quantity: 1 }],
            };
            if (e.target.dataset.sellingPlanId) {
                payload.items[0]['selling_plan'] = e.target.dataset.sellingPlanId;
            }
            if (e.target.dataset['4weekPlanId']) {
                payload.items[0]['selling_plan'] = e.target.dataset['4weekPlanId'];
            }
            try {
                const res = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify(payload),
                });
                await res.json();
                if (typeof Shopify !== 'undefined' && Shopify.openCartDrawer) {
                    Shopify.openCartDrawer();
                } else {
                    window.location.href = '/cart';
                }
            } catch (err) {
                console.error(err);
            } finally {
                e.target.disabled = false;
            }
        },
    }));
});

class MoodProductTabs {
    constructor(element) {
        this.element = element;
        this.tabs   = element.querySelectorAll('.mood-product-tabs__tab');
        this.panels = element.querySelectorAll('.mood-product-tabs__panel');
        this.mainButton = element.querySelector('.mood-product-tabs__button');

        // Track which panels have already been "unlocked" (iframes revealed)
        this._unlockedPanels = new Set();

        this.init();
    }

    init() {
        this.setupTabs();
        this.setupDragScroll();
        this.setupStickyNav();
        this.setupCSSOrdering();
        this.setupReviewsSlider();
        this.initAddToCart();

        // URL → active tab on first load
        this.restoreTabFromURL();

        // Unlock iframes only for the initially active panel
        const activePanel = this.element.querySelector('.mood-product-tabs__panel--active');
        if (activePanel) {
            this._unlockPanelIframes(activePanel);
            this._initPanelSlider(activePanel);
        }

        // On resize from mobile → desktop, load pending Frontrow iframes
        this._setupResizeObserver();
    }

    _setupResizeObserver() {
        let raf = null;
        const onResize = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = null;
                if (window.innerWidth < 768) return;
                const activePanel = this.element.querySelector('.mood-product-tabs__panel--active');
                if (activePanel) this._unlockPanelIframes(activePanel);
            });
        };
        window.addEventListener('resize', onResize);
    }

    // ─── URL sync ────────────────────────────────────────────────────────────

    /**
     * Read ?tab= from the URL and activate the matching tab.
     * Falls back to the first tab if param is absent or no match.
     */
    restoreTabFromURL() {
        const params  = new URLSearchParams(window.location.search);
        const tabParam = params.get('filter');
        if (!tabParam) return;

        const matchingTab = Array.from(this.tabs).find(
            t => t.dataset.tabFilter === tabParam
        );
        if (matchingTab) {
            this._activateTab(matchingTab, { updateURL: false });
        }
    }

    /**
     * Push the current active tab's handle to the URL as ?tab=<handle>.
     * Uses pushState so the browser back button works.
     */
    updateURL(tabFilter) {
        const params = new URLSearchParams(window.location.search);
        params.set('filter', tabFilter);
        window.history.pushState(
            { filter: tabFilter },
            '',
            `${window.location.pathname}?${params.toString()}`
        );
    }

    // ─── Lazy iframe unlock ───────────────────────────────────────────────────

    /**
     * Lazy panel strategy:
     * Inactive panels have their Frontrow iframes src set to a placeholder
     * (data-src). When a panel is first activated, we restore their src
     * so they load on demand instead of all at once.
     *
     * NOTE: this works together with the `loading="lazy"` attribute already
     * set on iframes in the snippet — that handles images/iframes in the
     * active panel that are below the fold. This method handles cross-panel
     * deferred loading.
     */
    _unlockPanelIframes(panel) {
        if (this._unlockedPanels.has(panel.id)) return;
        this._unlockedPanels.add(panel.id);

        // Only load Frontrow iframes on desktop to avoid mobile bloat
        if (window.innerWidth < 768) return;

        // Restore any iframes whose src was deferred with data-src
        panel.querySelectorAll('iframe[data-src]').forEach(iframe => {
            iframe.src = iframe.dataset.src;
            iframe.removeAttribute('data-src');
        });
    }

    // ─── Tabs ─────────────────────────────────────────────────────────────────

    setupTabs() {
        this.tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                this._activateTab(e.currentTarget, { updateURL: true });
            });
        });

        // Handle browser back/forward navigation
        window.addEventListener('popstate', (e) => {
            const tabFilter = e.state?.filter;
            if (!tabFilter) return;
            const matchingTab = Array.from(this.tabs).find(
                t => t.dataset.tabFilter === tabFilter
            );
            if (matchingTab) this._activateTab(matchingTab, { updateURL: false });
        });
    }

    _activateTab(tab, { updateURL = true } = {}) {
        const targetId = tab.dataset.tabTarget;
        const filter   = tab.dataset.tabFilter;

        // Update active states
        this.tabs.forEach(t => t.classList.remove('mood-product-tabs__tab--active'));
        this.panels.forEach(p => p.classList.remove('mood-product-tabs__panel--active'));

        tab.classList.add('mood-product-tabs__tab--active');

        const targetPanel = document.getElementById(targetId);
        if (targetPanel) {
            targetPanel.classList.add('mood-product-tabs__panel--active');
            // Lazy-unlock this panel's iframes on first activation
            this._unlockPanelIframes(targetPanel);
            // Lazy-init reviews slider for this panel
            this._initPanelSlider(targetPanel);
        }

        // Update "Shop All" button link
        if (this.mainButton && filter) {
            this.mainButton.href = `${this.mainButton.dataset.tabLink}?filter=${filter}`;
        }

        // Sync URL
        if (updateURL && filter) {
            this.updateURL(filter);
        }

        // Auto-scroll nav so the active tab is visible
        this._ensureTabVisible(tab);

        // Re-init Swiper reviews slider in case resize/relayout needed
        this._refreshReviewsSlider();
    }

    _ensureTabVisible(tab) {
        const nav = this.element.querySelector('.mood-product-tabs__nav');
        if (!nav) return;

        const tabRect = tab.getBoundingClientRect();
        const navRect = nav.getBoundingClientRect();

        const isFullyVisible =
            tabRect.left >= navRect.left &&
            tabRect.right <= navRect.right;

        if (isFullyVisible) return;

        // If the tab is overflowing on the left/right, scroll it into view
        // centering it roughly in the nav.
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    setupDragScroll() {
        const nav = this.element.querySelector('.mood-product-tabs__nav');
        if (!nav) return;

        let isDragging = false;
        let dragged    = false;
        let startX     = 0;
        let scrollLeft = 0;

        const dragStart = (e) => {
            isDragging = true;
            dragged    = false;
            startX     = (e.pageX || e.touches[0].pageX) - nav.offsetLeft;
            scrollLeft = nav.scrollLeft;
            nav.style.cursor     = 'grabbing';
            nav.style.userSelect = 'none';
        };

        const dragMove = (e) => {
            if (!isDragging) return;
            e.preventDefault();
            const x    = (e.pageX || e.touches[0].pageX) - nav.offsetLeft;
            const walk = x - startX;
            nav.scrollLeft = scrollLeft - walk;
            if (Math.abs(walk) > 5) dragged = true;
        };

        const dragEnd = () => {
            isDragging = false;
            nav.style.cursor     = '';
            nav.style.userSelect = '';
            if (dragged) {
                nav.style.pointerEvents = 'none';
                setTimeout(() => { nav.style.pointerEvents = ''; }, 200);
            }
        };

        nav.addEventListener('mousedown',  dragStart);
        nav.addEventListener('mousemove',  dragMove);
        nav.addEventListener('mouseup',    dragEnd);
        nav.addEventListener('mouseleave', dragEnd);
        // Mobile native touch scroll is handled by overflow-x: auto on the nav.
        // No custom touch handlers needed — they'd compete with native scroll.
    }

    // ─── Sticky nav ──────────────────────────────────────────────────────────

    setupStickyNav() {
        const navContainer = this.element.querySelector('.mood-product-tabs__nav-container');
        if (!navContainer) return;

        const findHeader = () => {
            const candidates = document.querySelectorAll('sticky-header-old, sticky-header, .header-wrapper, [data-header]');
            for (const el of candidates) {
                if (el.getBoundingClientRect().height > 0) return el;
            }
            return document.querySelector('header');
        };

        const setHeaderHeight = () => {
            const header = findHeader();
            if (!header) return;
            // Use the header's bottom edge relative to the viewport.
            // When the header is visible → bottom = its full height.
            // When scrolled down and hidden → bottom = 0.
            this.element.style.setProperty('--header-height', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
        };

        setHeaderHeight();
        window.addEventListener('resize', setHeaderHeight);
        window.addEventListener('scroll', setHeaderHeight, { passive: true });

        // Toggle shadow class via IntersectionObserver — when the nav
        // naturally becomes sticky (scrolled past its normal position),
        // add a visual indicator.
        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = '0';
        sentinel.style.left = '0';
        sentinel.style.width = '1px';
        sentinel.style.height = '1px';
        sentinel.style.pointerEvents = 'none';
        navContainer.parentNode.insertBefore(sentinel, navContainer);

        const observer = new IntersectionObserver(
            ([entry]) => {
                navContainer.classList.toggle('mood-product-tabs__nav-container--shadow', !entry.isIntersecting);
            },
            { threshold: 0 }
        );
        observer.observe(sentinel);
        this._navStickyObserver = observer;
    }

    // ─── CSS ordering ────────────────────────────────────────────────────────

    /**
     * CSS-order based positioning.
     * Reads data-slot from each card, pins cards with slot > 0,
     * fills gaps with free cards in natural order, applies style.order.
     *
     * Also handles the reviews banner (.collection__inline-reviews):
     * shifts card orders so the banner lands at its configured position
     * (read from its inline style.order set by Liquid).
     */
    setupCSSOrdering() {
        const grids = this.element.querySelectorAll('.mood-product-grid');

        grids.forEach(grid => {
            const cards  = Array.from(grid.querySelectorAll(':scope > [data-slot]'));
            const review = grid.querySelector(':scope > .collection__inline-reviews');
            if (!cards.length && !review) return;

            // Read the review's configured position from Liquid-set inline style
            let reviewPos = 0;
            if (review) {
                const v = parseInt(review.style.order, 10);
                if (!isNaN(v) && v > 0) reviewPos = v;
            }

            const pinned = new Map();
            const free   = [];

            cards.forEach(card => {
                const slot = parseInt(card.dataset.slot, 10) || 0;
                if (slot > 0) {
                    if (!pinned.has(slot)) {
                        pinned.set(slot, card);
                    } else {
                        free.push(card);
                    }
                } else {
                    free.push(card);
                }
            });

            const total   = cards.length;
            let freeIndex = 0;
            const ordered = [];

            for (let pos = 1; pos <= total; pos++) {
                if (pinned.has(pos)) {
                    ordered.push(pinned.get(pos));
                } else if (freeIndex < free.length) {
                    ordered.push(free[freeIndex++]);
                }
            }

            // Append any pinned cards whose slot exceeds the total (e.g. slot 10
            // when only 5 cards exist). Without this they stay at order:0 → top.
            pinned.forEach((card, slot) => {
                if (slot > total) ordered.push(card);
            });

            while (freeIndex < free.length) {
                ordered.push(free[freeIndex++]);
            }

            // Assign orders. Cards at or after the review's position are
            // shifted by +1 so the review lands at its configured spot.
            ordered.forEach((card, i) => {
                const base = i + 1;
                card.style.order = (reviewPos > 0 && base >= reviewPos) ? base + 1 : base;
            });

            if (review && reviewPos > 0) {
                review.style.order = reviewPos;
            }
        });
    }

    // ─── Reviews Swiper ──────────────────────────────────────────────────────

    setupReviewsSlider() {
        this._reviewsSliders = [];
        this._reviewsSliderInit = new Set();
    }

    _initPanelSlider(panel) {
        const sliderEl = panel.querySelector('[data-reviews-slider]');
        if (!sliderEl || this._reviewsSliderInit.has(sliderEl)) return;
        if (typeof Swiper === 'undefined') return;

        this._reviewsSliderInit.add(sliderEl);
        const swiper = new Swiper(sliderEl, {
            loop: true,
            spaceBetween: 16,
            centeredSlides: true,
            slidesPerView: 1,
            grabCursor: true,
            autoHeight: true,
            pagination: {
                el: sliderEl.querySelector('.swiper-pagination'),
                clickable: true,
            },
        });
        this._reviewsSliders.push(swiper);
        requestAnimationFrame(() => swiper.update());
    }

    _refreshReviewsSlider() {
        this._reviewsSliders.forEach(s => s.update());
    }

    // ─── Add to cart (legacy fallback, Alpine version is preferred) ──────────

    initAddToCart() {
        const atcButtons = this.element.querySelectorAll('.product-card__quick-add');
        atcButtons.forEach(btn => {
            btn.addEventListener('click', (e) => this.addToCart(e, btn));
        });
    }

    async addToCart(e, btn) {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        const payload = {
            items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        };

        if (btn.dataset.sellingPlanId) {
            payload.items[0]['selling_plan'] = btn.dataset.sellingPlanId;
        }
        if (btn.dataset['4weekPlanId']) {
            payload.items[0]['selling_plan'] = btn.dataset['4weekPlanId'];
        }

        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            await res.json();
            if (res.status !== 200) return;
            if (typeof Shopify !== 'undefined' && Shopify.openCartDrawer) {
                Shopify.openCartDrawer();
            } else {
                window.location.href = '/cart';
            }
        } catch (err) {
            console.error(err);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll('.mood-product-tabs');
    sections.forEach(section => new MoodProductTabs(section));
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target.querySelector('.mood-product-tabs');
        if (section) new MoodProductTabs(section);
    });
}
